import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Download, Upload, Trash2, IndianRupee, TrendingUp, TrendingDown } from 'lucide-react';
import { convertToCSV, downloadCSV } from '@/utils/csvExport';

const MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const DriverPayments = () => {
  const { API } = useAuth();
  const [payments, setPayments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_received: 0, received_count: 0, total_paid: 0, paid_count: 0, net: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('received');
  const [driverSearch, setDriverSearch] = useState('');
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);

  const [form, setForm] = useState({
    direction: 'received', date: new Date().toISOString().split('T')[0],
    driver_name: '', driver_id: '', driver_id_ext: '',
    vehicle_id: '', vehicle_number: '', vendor_name: '', vendor_id: '',
    amount: '', mode: 'cash', utr_no: '', remarks: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, driversRes, vehiclesRes, vendorsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/payments`, { params: { direction: activeTab } }),
        axios.get(`${API}/drivers`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/payments/summary`),
      ]);
      setPayments(paymentsRes.data);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
      setVendors(vendorsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [API, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Driver search
  useEffect(() => {
    if (driverSearch.length >= 2) {
      const filtered = drivers.filter(d =>
        d.name.toLowerCase().includes(driverSearch.toLowerCase()) ||
        d.driver_id.toLowerCase().includes(driverSearch.toLowerCase()) ||
        d.mobile.includes(driverSearch)
      );
      setFilteredDrivers(filtered);
      setShowDriverDropdown(true);
    } else {
      setFilteredDrivers([]);
      setShowDriverDropdown(false);
    }
  }, [driverSearch, drivers]);

  const selectDriver = (driver) => {
    setForm(prev => ({ ...prev, driver_name: driver.name, driver_id: driver.id, driver_id_ext: driver.driver_id }));
    setDriverSearch(driver.name);
    setShowDriverDropdown(false);
    // Auto-fetch vehicle from allocation
    const fetchAllocation = async () => {
      try {
        const res = await axios.get(`${API}/allotments`, { params: { status: 'active' } });
        const allot = res.data.find(a => a.driver_id === driver.id);
        if (allot) {
          const veh = vehicles.find(v => v.id === allot.vehicle_id);
          if (veh) {
            setForm(prev => ({ ...prev, vehicle_id: veh.id, vehicle_number: veh.vehicle_number }));
          }
        }
      } catch (e) { /* silent */ }
    };
    fetchAllocation();
  };

  const resetForm = () => {
    setForm({
      direction: activeTab, date: new Date().toISOString().split('T')[0],
      driver_name: '', driver_id: '', driver_id_ext: '',
      vehicle_id: '', vehicle_number: '', vendor_name: '', vendor_id: '',
      amount: '', mode: 'cash', utr_no: '', remarks: '',
    });
    setDriverSearch('');
  };

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.driver_name && !form.vendor_name) { toast.error('Select a driver or vendor'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments`, { ...form, amount: parseFloat(form.amount) });
      toast.success('Payment recorded');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/payments/csv-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadSample = async () => {
    try {
      const res = await axios.get(`${API}/payments/sample-csv`);
      const { headers, sample_row } = res.data;
      const csv = headers.join(',') + '\n' + sample_row.join(',') + '\n';
      downloadCSV(csv, 'payment_sample.csv');
    } catch { toast.error('Failed to download sample'); }
  };

  const handleDownloadAll = () => {
    if (payments.length === 0) { toast.error('No data to export'); return; }
    const headers = [
      { label: 'Payment ID', key: 'payment_id' }, { label: 'Direction', key: 'direction' },
      { label: 'Date', key: 'date' }, { label: 'Driver Name', key: 'driver_name' },
      { label: 'Driver ID', key: 'driver_id_ext' }, { label: 'Vehicle', key: 'vehicle_number' },
      { label: 'Vendor', key: 'vendor_name' }, { label: 'Amount', key: 'amount' },
      { label: 'Mode', key: 'mode' }, { label: 'UTR No', key: 'utr_no' }, { label: 'Remarks', key: 'remarks' },
    ];
    const csv = convertToCSV(payments, headers);
    downloadCSV(csv, `payments_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/payments/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="driver-payments-section">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Received</p>
                <p className="text-2xl font-bold text-emerald-700" data-testid="total-received">
                  {summary.total_received.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-400">{summary.received_count} entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg"><TrendingDown className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Paid</p>
                <p className="text-2xl font-bold text-red-700" data-testid="total-paid">
                  {summary.total_paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-400">{summary.paid_count} entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><IndianRupee className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Net Balance</p>
                <p className={`text-2xl font-bold ${summary.net >= 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="net-balance">
                  {summary.net.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Driver Payments</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => { resetForm(); setForm(f => ({ ...f, direction: activeTab })); setDialogOpen(true); }}
                className="bg-slate-900 text-white hover:bg-slate-800" data-testid="add-payment-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Payment
              </Button>
              <div className="relative">
                <Input type="file" accept=".csv,.xlsx,.xls" onChange={handleCSVUpload} className="hidden" id="csv-upload" disabled={uploading} />
                <Button size="sm" variant="outline" onClick={() => document.getElementById('csv-upload').click()} disabled={uploading} data-testid="csv-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} CSV Upload
                </Button>
              </div>
              <Button size="sm" variant="outline" onClick={handleDownloadSample} data-testid="download-sample-btn">
                <Download className="w-4 h-4 mr-1" /> Sample
              </Button>
              <Button size="sm" variant="outline" onClick={handleDownloadAll} data-testid="download-all-btn">
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4" data-testid="payment-tabs">
              <TabsTrigger value="received" data-testid="tab-received">Received</TabsTrigger>
              <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
            </TabsList>
            <TabsContent value={activeTab}>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="payments-table">
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">ID</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Driver</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mode</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">UTR</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Remarks</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((p) => (
                        <TableRow key={p.id} className="hover:bg-slate-50/50 border-b border-slate-100">
                          <TableCell className="font-mono text-xs text-slate-500">{p.payment_id}</TableCell>
                          <TableCell className="text-sm">{p.date}</TableCell>
                          <TableCell className="text-sm font-medium">{p.driver_name || p.driver_id_ext || '-'}</TableCell>
                          <TableCell className="text-xs font-mono">{p.vehicle_number || '-'}</TableCell>
                          <TableCell className="text-sm">{p.vendor_name || '-'}</TableCell>
                          <TableCell className="text-sm font-semibold">
                            {parseFloat(p.amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                          </TableCell>
                          <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{p.mode}</span></TableCell>
                          <TableCell className="text-xs font-mono">{p.utr_no || '-'}</TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{p.remarks || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(p.id)} data-testid={`del-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {payments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-12 text-slate-400 text-sm">No {activeTab} payments found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Enter payment details. Vehicle auto-fetched from current allocation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger data-testid="form-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-date" />
              </div>
            </div>

            <div className="space-y-1.5 relative">
              <Label>Driver Name <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Search driver by name, ID, or mobile..."
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
                onFocus={() => driverSearch.length >= 2 && setShowDriverDropdown(true)}
                data-testid="form-driver-search"
              />
              {showDriverDropdown && filteredDrivers.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {filteredDrivers.map((d) => (
                    <div key={d.id} className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                      onClick={() => selectDriver(d)} data-testid={`driver-option-${d.id}`}>
                      <span className="font-medium">{d.name}</span>
                      <span className="text-slate-500 ml-2">({d.driver_id})</span>
                      <span className="text-slate-400 ml-2">{d.mobile}</span>
                    </div>
                  ))}
                </div>
              )}
              {form.driver_id_ext && (
                <p className="text-xs text-emerald-600 mt-1">Driver ID: {form.driver_id_ext}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Vehicle (auto)</Label>
                <Input value={form.vehicle_number} readOnly placeholder="Auto from allocation" className="bg-slate-50" data-testid="form-vehicle" />
              </div>
              <div className="space-y-1.5">
                <Label>Vendor (optional)</Label>
                <Select value={form.vendor_id || 'none'} onValueChange={(v) => {
                  if (v === 'none') { setForm({ ...form, vendor_id: '', vendor_name: '' }); return; }
                  const vnd = vendors.find(x => x.id === v);
                  setForm({ ...form, vendor_id: v, vendor_name: vnd?.name || '' });
                }}>
                  <SelectTrigger data-testid="form-vendor"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Amount (INR) <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.01" placeholder="0.00" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="form-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>UTR No</Label>
                <Input placeholder="UTR/Transaction no." value={form.utr_no}
                  onChange={(e) => setForm({ ...form, utr_no: e.target.value })} data-testid="form-utr" />
              </div>
              <div className="space-y-1.5">
                <Label>Remarks</Label>
                <Input placeholder="Notes" value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="form-remarks" />
              </div>
            </div>

            <Button className="w-full bg-slate-900 text-white hover:bg-slate-800" onClick={handleSubmit}
              disabled={submitting} data-testid="submit-payment-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverPayments;
